import { FileWithPath } from 'react-dropzone';
import { EntityId } from '@reduxjs/toolkit';

import { ListEntity } from '@shared/components/list-view-v2/types';
import { ALL, DocumentContentType, OWNED, RECEIVED, SHARED, SHARED_BY_NETWORK } from '@helpers';
import { ListOnItemsRenderedProps } from 'react-window';
import { Sort } from '@utils/types';

export const LIST = 'list';
export const GRID = 'grid';
export type FilesViewType = typeof LIST | typeof GRID;

export type FileAccessType =
  | typeof ALL
  | typeof OWNED
  | typeof RECEIVED
  | typeof SHARED
  | typeof SHARED_BY_NETWORK;

export const OWNER = 'owner';
export const CONTRIBUTOR = 'contributor';
export const VIEWER = 'viewer';
export const EDITOR = 'editor';
export type FileAccessStatus = typeof OWNER | typeof CONTRIBUTOR | typeof EDITOR | typeof VIEWER;

export const ROOT_DIRECTORY = '.';

export const ACCEPTABLE_FILE_TYPES =
  '.PNG,.JFIF,.JPG,.JPEG,.AVI,.MP4,.MP3,.MOV,.FLAC,.PDF,.DOCX,.DOC,.PPT,.PPTX,.TIFF,.TIF,.TXT,.XLS,.XLSX,.HTML';

export type AcceptableFileExtentions =
  | '.PNG'
  | '.JFIF'
  | '.JPG'
  | '.JPEG'
  | '.AVI'
  | '.MP4'
  | '.MP3'
  | '.MOV'
  | '.FLAC'
  | '.PDF'
  | '.DOCX'
  | '.DOC'
  | '.PPT'
  | '.PPTX'
  | '.TIF'
  | '.TIFF'
  | '.TXT'
  | '.XLS'
  | '.XLSX'
  | '.HTML';

export const MIN_GRID_ITEM_SIZE = 200;
export const MAX_GRID_ITEM_SIZE = 576;

export interface File extends ListEntity {
  last_updated: string;
  user_id: string;
  parent_dir_id: string;
  content_type: DocumentContentType;
  file_size?: number;
  file_id: string;
  created: string;
  description: string;
  name: string;
  is_in_trash: boolean;
  access_status: FileAccessStatus;
  url: string;
  thumbnail_url: string;
  upload_part_sizes?: string[];
}

export interface FileBreadcrumb {
  id: string;
  name: string;
  isFile: boolean;
  accessStatus?: FileAccessStatus;
  isUser?: boolean;
  onClick?: () => void;
}

export interface ResponseParams {
  error: string;
  message: string;
}

export interface FetchFilesResponse extends ResponseParams {
  files?: File[];
  url?: string;
}

export interface UploadFileProgressData {
  name: string;
  size: number;
  isImage: boolean;
  previewUrl: string;
  total: number;
  ready: number;
}

export type UploadFilesProgressData = {
  [key: string]: UploadFileProgressData;
};

export interface UploadPart {
  number: number;
  size: number;
  url: string;
}

export interface UploadedFile {
  file_id: string;
  upload_id: string;
  upload_parts: UploadPart[];
  upload_size: number;
  user_id: string;
}

export interface UploadFilesResponse {
  payload: UploadedFile[];
}

interface CreatedDirectory {
  file_id: string;
  user_id: string;
}

export interface CreateDirectoryResponse extends ResponseParams {
  payload: CreatedDirectory[];
}

export interface DownloadIdResponse {
  download_id: string;
}

export interface DownloadUrlResponse {
  status: 'Pending' | 'Ready';
  url?: string;
}

export interface CopyUrlResponse {
  completed: number;
  operation_id: string;
  target_quantity: number;
}

export interface FilesRequestParameters {
  description?: string;
  name?: string;
  dir_id?: string;
}

type FileTypePayload = 'SINGLE_PAYLOAD' | 'EXTENSIVE_PAYLOAD';

export interface ExtensiveFilesRequest<T> {
  files: T;
  parameters: FilesRequestParameters;
}
export interface FilesRequest<T, Payload extends FileTypePayload = 'SINGLE_PAYLOAD'> {
  type?: 'MODIFY' | 'MOVE' | 'COPY' | 'SHARE' | 'SOFT_DELETE' | 'HARD_DELETE' | 'RESTORE';
  payload: Payload extends 'EXTENSIVE_PAYLOAD'
    ? ExtensiveFilesRequest<T>
    : Payload extends 'SINGLE_PAYLOAD'
    ? T
    : never;
}

export interface FilesOperationRequestItem {
  file_id: string;
  share_id?: string;
}

export interface FilesOperationRequest<Payload extends FileTypePayload = 'SINGLE_PAYLOAD'>
  extends FilesRequest<FilesOperationRequestItem[], Payload> {}

export interface ShareFilesRequestItem {
  first_name: string;
  last_name: string;
  target_email: string;
  permission: string;
  shared_files: string[];
}

export interface ShareFilesRequest<Payload extends FileTypePayload = 'SINGLE_PAYLOAD'>
  extends FilesRequest<ShareFilesRequestItem[], Payload> {}

export interface UploadFilesMap {
  [key: string]: UploadFileData;
}

export interface UploadFileCompleteRequestItem {
  file_id: string;
  fileName: string;
  fileType: string;
  filesize: number;
  user_id: string;
  upload_id: string;
  upload_results: {
    part_number: number;
    etag: string;
  }[];
}

export interface UploadFileCompleteRequest<Payload extends FileTypePayload = 'SINGLE_PAYLOAD'>
  extends FilesRequest<UploadFileCompleteRequestItem[], Payload> {}

export interface FileWithContent {
  name: string;
  type: DocumentContentType;
  size: number;
  content?: FileWithPath;
}

export interface FileTreeNode extends FileWithContent {
  children: FileTreeNode[];
}

export interface NestedFileNodes {
  fileNodes: FileTreeNode[];
  [key: string]: FileTreeNode[] | undefined;
}

export interface TempFiles {
  fileNodes: FileTreeNode[];
  [key: string]: FileTreeNode[] | undefined | NestedFileNodes;
}

export interface UploadFileData extends FileWithContent {
  id: string;
  parentId: string;
}

export interface UploadFileRequestItem {
  file_id: string;
  parent_dir_id: string;
  name: string;
  description: string;
  content_type: DocumentContentType;
  upload_size?: number;
}

export interface UploadFilesRequest {
  user_id: string;
  files: UploadFileRequestItem[];
}

export interface UserWithAccess {
  user_id: string;
  given_name: string;
  family_name: string;
  email: string;
  access_status: string;
}

export interface Comment {
  user_id: string;
  given_name: string;
  family_name: string;
  email: string;
  access_status: string;
  message: string;
  date: string;
}
export interface CommentUserData {
  commentor_id: string;
  email: string;
  family_name: string;
  given_name: string;
}

export interface CommentEmoticons {
  emoticon: string;
  commentor_ids: CommentUserData[];
}

export interface CommentEmoticonsSet {
  emoticon: string;
  action: 'add' | 'remove';
}

export interface FileCommentsResponse {
  comment_id: string;
  owner_id: string;
  commentor_id: string;
  file_id: EntityId;
  comment_text: string;
  emoticons: CommentEmoticons[];

  given_name: string;
  family_name: string;
  comment_date: string;
}

export interface FileDetailsResponse {
  description: string;
  file_id: EntityId;
  message: string;
  users_with_access: UserWithAccess[];
}

export interface DeleteUserDataResponse extends ResponseParams {
  users: string[];
}

export type ExportUsersResponse = DownloadIdResponse & ResponseParams;

export interface ExportUsersParams {
  usersIds: EntityId[];
  userRole: string;
}

export interface ModifyFileResult {
  file_id: string;
  result: string;
}

export interface VirtualizedFilesGridProps {
  data: File[];
  onFileClick: (file: ListEntity) => void;
  setScrollPosition: (newScrollPosition: number) => void;

  saveScrollPosition?: () => void;
  readOnly?: boolean;
}

export interface VirtualizedGridListItemProps extends Omit<VirtualizedFilesGridProps, 'data'> {
  filesPage: VirtualizedFilesGridProps['data'];
  width: number;
  height: number;
  onItemsRendered: (props: ListOnItemsRenderedProps) => any;
  gridItemSize: number;
  sort: Sort;

  saveScrollPosition?: () => void;
}

export type OmitVirtualizedGridRowItemProps =
  | 'width'
  | 'height'
  | 'setScrollPosition'
  | 'onItemsRendered'
  | 'selectedFiles'
  | 'onSelect';
export interface VirtualizedGridRowItemProps
  extends Omit<VirtualizedGridListItemProps, OmitVirtualizedGridRowItemProps> {
  data: {
    files: File[];
    selectedIds: EntityId[];
    numItemsPerRow: number;
    onSelect?: (file: File) => void;
    gridItemSize: number;
    onFileClick: (file: ListEntity) => void;

    saveScrollPosition?: () => void;
    readOnly?: boolean;
    selectMode?: string | undefined;
  };
  index: number;
  style: React.CSSProperties;
  saveScrollPosition: () => void;
}
